import type { SimEvent } from "../protocol/events";
import { pollute } from "./pollution";
import { accrueVerminRisk, facilityMitigationOf, type EngineState } from "./state";

/**
 * 03-hazards.md §3.2/§3.4 (spec 0006): the two event-driven facility hazards.
 * Both are turn-indexed, authored on `StageDef.scriptedEvents`, and fire
 * exactly once each — `state.firedScriptedEvents` is the guard, the same
 * shape as `pollutionSources`. Neither introduces a new hazard counter: a
 * heatwave and a seismic event write straight into the existing
 * `node.heatCounter` / `edge.pressureCounter` fields the rest of `hazards.ts`
 * already owns.
 */

/** Dispatched from tick step 1, before inputs emit, once per turn's first tick. */
export function dispatchScriptedEvents(
  state: EngineState,
  events: SimEvent[],
): { seismicFiredThisTick: boolean } {
  // A turn's Run phase can run many ticks; both the waste-buyer vermin
  // accrual below and the scripted-event dispatch that follows fire on the
  // turn's first tick only.
  if (state.tickInTurn !== 0) return { seismicFiredThisTick: false };

  // §3.3 / spec 0023 Open Question 3: each wasteBuyer on the board without a
  // Lid attachment adds +1 verminRisk per turn (not per tick), accrued here
  // rather than in accumulateVermin (hazards.ts, step 5), which only fires
  // once per TICK — this hook already runs once per TURN.
  const unsealedWasteBuyers = state.nodes
    .filter(
      (n) =>
        n.kind === "wasteBuyer" &&
        !n.isDestroyed &&
        !n.attachments.some((a) => a.effect.kind === "lid"),
    )
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  for (const node of unsealedWasteBuyers) {
    accrueVerminRisk(state, node.id, events);
  }

  if (state.stage.scriptedEvents === undefined) return { seismicFiredThisTick: false };

  let seismicFiredThisTick = false;

  for (const def of state.stage.scriptedEvents) {
    if (def.turn !== state.turn) continue;

    const key = `${def.turn}:${def.kind}`;
    if (state.firedScriptedEvents.includes(key)) continue;
    state.firedScriptedEvents.push(key);

    switch (def.kind) {
      case "powerSurge": {
        // §3.2 / Open Question 3: pumps and chillers freeze — flowMover
        // nodes, thermalRegulator nodes, and anything wearing a cooling
        // jacket, deduplicated and sorted so the announcement is stable.
        const frozenIds = new Set<string>();
        for (const node of state.nodes) {
          if (node.isDestroyed) continue;
          if (node.kind === "flowMover" || node.kind === "thermalRegulator") frozenIds.add(node.id);
          else if (node.attachments.some((a) => a.effect.kind === "coolingJacket")) frozenIds.add(node.id);
        }
        const frozenNodeIds = [...frozenIds].sort();

        // Spec 0028: Surge Protector answers the freeze only — the surge
        // still fires, still blacks out next Play's kW, and is still
        // announced below exactly as before (Resolved Question 1).
        if (!facilityMitigationOf(state, "surgeProtector")) {
          state.powerFreezeTicks = 1;
        }
        state.isBlackoutPending = true;

        events.push({
          type: "facility.powerOutage",
          cause: "scripted",
          frozenNodeIds,
          availableKw: state.energyBudgetKw - state.energySpentKw,
        });
        pollute(state, "powerOutage", `facility-turn-${state.turn}`, events);
        break;
      }

      case "heatwave": {
        // §3.4 / Open Question 4: a standing modifier, not an incident — the
        // per-tick `+1` itself is applied by `applyHeatwaveHeat`, every tick
        // from here to the end of the stage.
        state.isHeatwaveActive = true;
        const affectedIds = state.nodes
          .filter((n) => !n.isDestroyed)
          .map((n) => n.id)
          .sort();

        events.push({ type: "facility.disaster", kind: "heatwave", affectedIds });
        pollute(state, "structuralShear", `facility-turn-${state.turn}`, events);
        break;
      }

      case "seismic":
        // One-shot: the pressure write itself happens in step 4
        // (`applySeismicPressure`), so the edges considered "filled" reflect
        // this tick's post-movement state, not the state before it.
        seismicFiredThisTick = true;
        break;
    }
  }

  return { seismicFiredThisTick };
}

/** Step 4, after `accumulatePressure`: a seismic event's one-shot `+1` to every filled, non-burst edge. */
export function applySeismicPressure(state: EngineState, fired: boolean, events: SimEvent[]): void {
  if (!fired) return;

  // Spec 0028: Seismic Isolation Mounts answers the `+1` itself, not the
  // event — the telegraph (facility.disaster, pollute) still fires below,
  // but affectedIds reports what actually happened, so it comes back empty.
  const mounted = facilityMitigationOf(state, "seismicMounts") !== undefined;
  const affectedIds: string[] = [];
  const edges = [...state.edges].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const edge of edges) {
    if (edge.isBurst || edge.transit.length === 0 || mounted) continue;

    edge.pressureCounter += 1;
    events.push({ type: "pipe.pressured", edgeId: edge.id, pressureCounter: edge.pressureCounter });
    affectedIds.push(edge.id);
  }

  events.push({ type: "facility.disaster", kind: "seismic", affectedIds });
  pollute(state, "structuralShear", `facility-turn-${state.turn}`, events);
}

/** Step 5, before `accumulateHeat`: a standing heatwave's `+1` to every live node. */
export function applyHeatwaveHeat(state: EngineState, events: SimEvent[]): void {
  if (!state.isHeatwaveActive) return;
  // Spec 0028 (Constraints): HVAC Chillers cancels the heatwave's own `+1`
  // facility-wide by contributing nothing here — never a second `heatShedOf`
  // cancellation path, so a jacketed node under a heatwave with chillers
  // installed sheds exactly what it would with no heatwave at all.
  if (facilityMitigationOf(state, "hvacChiller")) return;

  for (const node of state.nodes) {
    if (node.isDestroyed) continue;
    // Q10: a tank, hopper or vent (spec 0032) has no process heat to build up
    // — a heatwave bakes the rig's hardware, not its storage.
    if (node.kind === "inputTank" || node.kind === "productHopper" || node.kind === "wasteBuyer" || node.kind === "vent") continue;

    node.heatCounter += 1;
    events.push({
      type: "node.heated",
      nodeId: node.id,
      heatCounter: node.heatCounter,
      criticalTemp: node.criticalTemp,
    });
  }
}
